import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useEmployeeCustomFieldsHook from "@/hooks/use-employee-custom-fields.hook.tsx";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import { ApiCFTabType, ApiSection, ApiSectionTypes } from "@/types/custom-field.types.ts";
import { getJobEmoji } from "@/types/recruit/job.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { FontAwesomeIcon, ListItem, Skeleton, Stack, Title } from "@skailer-com/uikit";
import { ReactNode, useMemo } from "react";
import { Link } from "react-router-dom";
import usePermissions from "@/hooks/use-permissions.hook.ts";

interface EmployeePersonalCardVSection {
  section: ApiSection;
  items: { fieldId?: number; node: ReactNode }[];
}

interface EmployeePersonalCardProps extends EmployeePageComponentProps {
  openLinksInNewTab?: boolean;
}

export function EmployeePersonalCard({ employee, openLinksInNewTab }: EmployeePersonalCardProps) {
  const { data: appConfig, isLoading: appConfigIsLoading } = useAppConfig();
  const { renderEmployeeCustomFieldListItem } = useEmployeeCustomFieldsHook();
  const { getSectionTitle } = useCustomFieldsHook();
  const { canIView } = usePermissions();

  const information = useMemo(() => {
    const result: EmployeePersonalCardVSection[] = [];

    const sidebarTab = appConfig?.ui.employee_profile.tabs.find(
      (tabWrapper) => tabWrapper.tab.type === ApiCFTabType.sidebar
    );

    sidebarTab?.items.forEach((item) => {
      if (!item.section) return;

      const section: EmployeePersonalCardVSection = {
        section: item.section.section,
        items: [],
      };

      if (section.section.section_type == ApiSectionTypes.employee_basic_info_hired_via_job) {
        if (canIView(ApiPermission.recruiting_module) && employee.hired_details) {
          const { applicant, job } = employee.hired_details;
          const hiredViaJobLink = `/recruitment/candidates/${applicant.id}?job_id=${job.id}`;
          const applicantName = [applicant.first_name, applicant.last_name]
            .filter((name) => name?.trim())
            .join(" ");

          section.items.push({
            node: (
              <Link
                to={hiredViaJobLink}
                className={"pt-2"}
                target={openLinksInNewTab ? "_blank" : undefined}
                rel={openLinksInNewTab ? "noopener noreferrer" : undefined}
              >
                <ListItem
                  preTitle={applicantName || "-"}
                  title={job.title || "-"}
                  avatarUrl={applicant.photo_url}
                  emoji={getJobEmoji(job)}
                  behavior={"card"}
                  forceShowAvatar={true}
                  valueSlot={<FontAwesomeIcon icon={"fa-light fa-chevron-right"} />}
                />
              </Link>
            ),
          });
        }
        result.push(section);
        return;
      }

      item.section.fields.forEach((field) => {
        const rendered: ReactNode | undefined = renderEmployeeCustomFieldListItem(
          field,
          employee,
          openLinksInNewTab
        );

        if (rendered) section.items.push({ fieldId: field.id, node: rendered });
      });

      result.push(section);
    });

    return result;
  }, [appConfig, canIView, employee, openLinksInNewTab, renderEmployeeCustomFieldListItem]);

  return (
    <Stack>
      {appConfigIsLoading && (
        <Stack className={"py-2"} gap={"md"}>
          <Skeleton className={"h-5 w-full"} />
          <Skeleton className={"h-5 w-full"} />
        </Stack>
      )}
      {information.map((sectionWrapper, index) => {
        if (sectionWrapper.items.length === 0) return null;

        return (
          <Stack
            id={`section-${sectionWrapper.section.id}`}
            key={`section-${sectionWrapper.section.id}`}
          >
            <Title
              header={getSectionTitle(sectionWrapper.section)}
              paddingTop={index !== 0}
              size={"xs"}
              variant={"secondary"}
            />
            {sectionWrapper.items.map(({ fieldId, node }, index) => (
              <Stack id={fieldId ? `field-${fieldId}` : undefined} key={`personal-${index}`}>
                {node}
              </Stack>
            ))}
          </Stack>
        );
      })}
    </Stack>
  );
}
